import { App, Badge, Divider, Drawer, Flex, Popover } from "antd";
import { useTranslation } from "react-i18next";
import { BellIcon, MessageCheckSquareIcon } from "../../../../assets/icons";
import { Button } from "../../../components/CustomAntdComponents/Button";
import { InfoCircleOutlined } from "@ant-design/icons";
import { useState, memo } from "react";
import { useNavigate } from "react-router-dom";
import {
  useUserNotifications,
  useUnreadCount,
  useMarkAllAsRead,
  useMarkNotificationAsRead,
  useNotification,
} from "../../../../api/services/notification.hooks";
import type { UserNotification } from "../../../../api/services/notification.type";
import { resolveNotificationPath } from "../../../../utils/notificationNavigation";
import { sanitizeHtml } from "../../../../utils/sanitizeHtml";
import styles from "./NotificationsMenu.module.css";
import { trackNotificationClicked } from "../../../../analytics/productEvents";

const NotificationMenuContent = memo(() => {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const navigate = useNavigate();
  const [selectedNotificationId, setSelectedNotificationId] = useState<
    string | null
  >(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  // Fetch user notifications
  const { data: notificationsResponse, isLoading: isLoadingNotifications } =
    useUserNotifications({
      page: 1,
      limit: 10,
    });

  // Fetch unread count
  const { data: unreadCountResponse } = useUnreadCount();

  // Mutations
  const markAllAsReadMutation = useMarkAllAsRead();
  const markAsReadMutation = useMarkNotificationAsRead();

  // Fetch selected notification details
  const { data: selectedNotificationResponse } = useNotification(
    selectedNotificationId || "",
  );

  // WebSocket updates are handled by NotificationWebSocketProvider at Layout level

  const notifications = notificationsResponse?.data || [];
  const unreadCount = unreadCountResponse?.data?.unread_count || 0;

  const handleMarkAllAsRead = async () => {
    try {
      await markAllAsReadMutation.mutateAsync();
      message.success(t("pages.notifications.markAllSuccess"));
    } catch {
      message.error(t("pages.notifications.markAllError"));
    }
  };

  const handleNotificationClick = async (notification: UserNotification) => {
    if (!notification.is_read) {
      try {
        await markAsReadMutation.mutateAsync(notification.notification.id);
      } catch (error) {
        console.error("Failed to mark notification as read:", error);
      }
    }

    const targetPath = await resolveNotificationPath(notification.notification);
    trackNotificationClicked({
      notification_type:
        notification.notification.action_type ||
        String(notification.notification.type),
      destination: targetPath ?? undefined,
      resolved: Boolean(targetPath),
    });
    if (targetPath) {
      navigate(targetPath);
      return;
    }

    setSelectedNotificationId(notification.notification.id);
    setIsDrawerOpen(true);
  };

  const selectedNotification = selectedNotificationResponse?.data;

  return (
    <div className={styles.popoverContent}>
      <div className={styles.header}>
        <div className={styles.heading}>{t("pages.notifications.title")}</div>
        {unreadCount > 0 && <div className={styles.badge}>{unreadCount}</div>}
        {unreadCount > 0 && (
          <div
            className={styles.markAll}
            onClick={handleMarkAllAsRead}
            style={{
              cursor: markAllAsReadMutation.isPending
                ? "not-allowed"
                : "pointer",
              opacity: markAllAsReadMutation.isPending ? 0.6 : 1,
            }}
          >
            <MessageCheckSquareIcon />
            {t("pages.notifications.markAllRead")}
          </div>
        )}
      </div>

      <div className={styles.notificationList}>
        {isLoadingNotifications ? (
          <div className={styles.stateMessage}>
            {t("pages.notifications.loading")}
          </div>
        ) : notifications.length === 0 ? (
          <div className={styles.stateMessage}>
            {t("pages.notifications.empty")}
          </div>
        ) : (
          notifications.map((userNotification, index) => {
            const notification = userNotification.notification;
            return (
              <div key={notification.id}>
                {!!index && <Divider className={styles.divider} />}
                <div
                  className={styles.notificationCard}
                  onClick={() => handleNotificationClick(userNotification)}
                  style={{
                    cursor: "pointer",
                    opacity: userNotification.is_read ? 0.7 : 1,
                  }}
                >
                  <div>
                    <Button
                      icon={
                        <InfoCircleOutlined
                          className={styles.notificationIcon}
                        />
                      }
                    />
                  </div>
                  <div style={{ flex: 1 }}>
                    <div className={styles.title}>{notification.title}</div>
                    <div
                      className={styles.desc}
                      dangerouslySetInnerHTML={{
                        __html: sanitizeHtml(notification.description),
                      }}
                    />
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      <Drawer
        title={t("pages.notifications.drawerTitle")}
        placement="right"
        width={500}
        open={isDrawerOpen}
        onClose={() => {
          setIsDrawerOpen(false);
          setSelectedNotificationId(null);
        }}
        footer={
          <Flex justify="end">
            <Button
              onClick={() => {
                setIsDrawerOpen(false);
                setSelectedNotificationId(null);
              }}
            >
              {t("pages.notifications.close")}
            </Button>
          </Flex>
        }
      >
        {selectedNotification ? (
          <div>
            <div className={styles.title}>{selectedNotification.title}</div>
            <div
              className={styles.descDetail}
              dangerouslySetInnerHTML={{
                __html: sanitizeHtml(selectedNotification.description),
              }}
            />
          </div>
        ) : (
          <div className={styles.stateMessage}>
            {t("pages.notifications.loadingDetail")}
          </div>
        )}
      </Drawer>
    </div>
  );
});

NotificationMenuContent.displayName = "NotificationMenuContent";

export const NotificationsMenu = () => {
  const { data: unreadCountResponse } = useUnreadCount();
  const unreadCount = unreadCountResponse?.data?.unread_count || 0;

  return (
    <>
      <Popover
        content={<NotificationMenuContent />}
        trigger="click"
        placement="bottomLeft"
        overlayInnerStyle={{ padding: 0 }}
        overlayStyle={{ maxWidth: "450px" }}
      >
        <Badge
          count={unreadCount > 0 ? unreadCount : 0}
          size="small"
          style={{ background: "#17B26A" }}
        >
          <BellIcon className={styles.notificationIcon} />
        </Badge>
      </Popover>
    </>
  );
};
